import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'md' | 'lg';
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, Props>(
  ({ variant = 'primary', size = 'md', loading, disabled, className, children, ...props }, ref) => {
    const variants = {
      primary: 'bg-brand-700 text-white hover:bg-brand-800 active:bg-brand-900',
      secondary: 'bg-white text-brand-700 border border-slate-300 hover:bg-slate-50',
      ghost: 'bg-transparent text-brand-700 hover:bg-slate-100',
      danger: 'bg-red-600 text-white hover:bg-red-700',
    };
    const sizes = {
      md: 'h-11 px-4 text-sm',
      lg: 'min-h-touch px-6 text-base', // Aussendienst: grosse Ziele
    };
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors',
          'disabled:opacity-50 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
          variants[variant],
          sizes[size],
          className,
        )}
        {...props}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';
